/**
 * Transformaciones del dump completo de la sesión (output de
 * `fetchSessionFull`) en distintas "vistas" enfocadas — cada una pensada
 * para un caso de uso de copy-to-clipboard concreto:
 *
 *   full      → JSON crudo, sin filtrar.
 *   transcript → solo turns con texto + timing.
 *   prompts    → prompt_logs por turn (input al LLM + response).
 *   costs      → breakdown financiero (total, por turn, por kind/provider).
 *   timing     → latencias y tiempos para análisis de performance.
 *   errors     → spans con is_error=true + contexto del turn.
 *   audio      → manifest de audios persistidos (qué hay grabado).
 *   claude     → bundle para enviárselo a una IA para debugging.
 *   markdown   → conversación renderizada como texto plano markdown.
 *
 * El dump full viene tipado como `unknown` (el endpoint no expone tipos
 * estrictos); acá hacemos narrowing defensivo. Si alguna sección no está
 * presente devolvemos arrays/objetos vacíos sin tirar.
 */
export type CopyView =
  | 'full'
  | 'transcript'
  | 'prompts'
  | 'costs'
  | 'timing'
  | 'errors'
  | 'audio'
  | 'claude'
  | 'markdown';

interface SessionDump {
  exportedAt?: string;
  session?: {
    id?: string;
    userId?: string;
    userEmail?: string;
    moduleId?: string | null;
    startedAt?: string;
    endedAt?: string | null;
    endedReason?: string | null;
    metadata?: Record<string, unknown>;
    totalCostNano?: string;
  };
  turns?: Array<{
    id: string;
    turnIndex: number;
    userText: string | null;
    assistantText: string | null;
    startedAt: string;
    endedAt: string;
    durationMs: number;
    timeToFirstAudioMs: number | null;
    interrupted: boolean;
  }>;
  llmCalls?: Array<{
    id: string;
    turnId: string | null;
    provider: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    cacheCreateTokens: number;
    cacheReadTokens: number;
    latencyMs: number | null;
    createdAt: string;
  }>;
  spans?: Array<{
    id: string;
    parentId: string | null;
    kind: string;
    name: string;
    startedAt: string;
    endedAt: string;
    durationMs: number;
    costNano: string | null;
    isError: boolean;
    errorKind: string | null;
    attributes: Record<string, unknown>;
  }>;
  audio?: Array<{
    turnIndex: number;
    kind: 'user' | 'assistant';
    contentType: string;
    durationMs: number | null;
    sampleRate: number | null;
    bytes: number;
  }>;
  promptLogs?: Array<{
    id: string;
    turnId: string | null;
    systemPrompt: string;
    /**
     * Bloques que cambian por turno (level/unit/memory/pronunciation_report).
     * Persistido en `prompt_logs.dynamic_system` por migration 0047. Null
     * cuando el módulo no usa dynamicSystem o el wrapper devolvió vacío.
     */
    dynamicSystem?: string | null;
    messages: unknown;
    responseText: string | null;
    /**
     * Output del structured-output tool (`submit_turn` en languages) por turn.
     * Shape: `{corrections?, pronunciation_corrections?}`. Null si el turn
     * no produjo correcciones o si el módulo no usa structured tools.
     * Persistido en `prompt_logs.tool_calls` por migration 0046.
     */
    toolCalls?: {
      corrections?: Array<{ said: string; fix: string; explanation: string | null }>;
      pronunciation_corrections?: Array<{
        word: string;
        said_ipa: string;
        target_ipa: string;
        tip: string;
        detail: string | null;
      }>;
    } | null;
    createdAt: string;
  }>;
}

const NANO_PER_USD = 1_000_000_000;

function asDump(value: unknown): SessionDump {
  if (!value || typeof value !== 'object') return {};
  return value;
}

function nanoToUsd(nano: string | null | undefined): number {
  if (!nano) return 0;
  const n = Number(nano);
  if (!Number.isFinite(n)) return 0;
  return n / NANO_PER_USD;
}

function sumNano(values: Array<string | null | undefined>): string {
  let total = 0n;
  for (const v of values) {
    if (!v) continue;
    try {
      total += BigInt(v);
    } catch {
      /* skip */
    }
  }
  return total.toString();
}

function attrNumber(attrs: Record<string, unknown>, key: string): number | null {
  const v = attrs?.[key];
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function attrString(attrs: Record<string, unknown>, key: string): string | null {
  const v = attrs?.[key];
  return typeof v === 'string' ? v : null;
}

/** Pretty-print con indent 2 + agrega nota de versión. */
function pretty(obj: unknown): string {
  return JSON.stringify(obj, null, 2);
}

export function deriveCopyPayload(view: CopyView, full: unknown): string {
  const dump = asDump(full);
  switch (view) {
    case 'full':
      return pretty(full);
    case 'transcript':
      return pretty(toTranscript(dump));
    case 'prompts':
      return pretty(toPromptsResponses(dump));
    case 'costs':
      return pretty(toCosts(dump));
    case 'timing':
      return pretty(toTiming(dump));
    case 'errors':
      return pretty(toErrors(dump));
    case 'audio':
      return pretty(toAudio(dump));
    case 'claude':
      return pretty(toClaudeBundle(dump));
    case 'markdown':
      return toMarkdown(dump);
  }
}

function toTranscript(d: SessionDump): unknown {
  return {
    session: d.session,
    turns: (d.turns ?? []).map((t) => ({
      turnIndex: t.turnIndex,
      userText: t.userText,
      assistantText: t.assistantText,
      startedAt: t.startedAt,
      endedAt: t.endedAt,
      durationMs: t.durationMs,
      interrupted: t.interrupted,
    })),
  };
}

function toPromptsResponses(d: SessionDump): unknown {
  const turnsById = new Map((d.turns ?? []).map((t) => [t.id, t]));
  const llmByTurn = new Map<string, NonNullable<SessionDump['llmCalls']>[number]>();
  for (const lc of d.llmCalls ?? []) {
    if (lc.turnId) llmByTurn.set(lc.turnId, lc);
  }
  const items = (d.promptLogs ?? []).map((pl) => {
    const turn = pl.turnId ? turnsById.get(pl.turnId) : null;
    const llm = pl.turnId ? llmByTurn.get(pl.turnId) : null;
    return {
      turnIndex: turn?.turnIndex ?? null,
      turnId: pl.turnId,
      provider: llm?.provider ?? null,
      model: llm?.model ?? null,
      tokens: llm
        ? {
            input: llm.inputTokens,
            output: llm.outputTokens,
            cacheRead: llm.cacheReadTokens,
            cacheWrite: llm.cacheCreateTokens,
          }
        : null,
      latencyMs: llm?.latencyMs ?? null,
      userText: turn?.userText ?? null,
      systemPrompt: pl.systemPrompt,
      messages: pl.messages,
      responseText: pl.responseText,
      createdAt: pl.createdAt,
    };
  });
  return {
    session: d.session,
    note:
      items.length === 0
        ? 'No prompt_logs persisted — la sesión pudo haber corrido con recording.transcript=false'
        : undefined,
    items,
  };
}

function toCosts(d: SessionDump): unknown {
  const spans = d.spans ?? [];

  // Costo total: suma de spans NO agregadores (excluye session/turn/composite).
  const AGGREGATORS = new Set(['session', 'turn', 'composite']);
  const leafCosts = spans.filter((s) => !AGGREGATORS.has(s.kind)).map((s) => s.costNano);
  const totalNano = sumNano(leafCosts);

  // Por kind.
  const byKind: Record<string, { totalNano: string; calls: number }> = {};
  for (const s of spans) {
    if (AGGREGATORS.has(s.kind)) continue;
    if (!s.costNano) continue;
    const acc = byKind[s.kind] ?? { totalNano: '0', calls: 0 };
    acc.totalNano = sumNano([acc.totalNano, s.costNano]);
    acc.calls += 1;
    byKind[s.kind] = acc;
  }

  // Por provider+model (solo kinds que tienen esos atributos).
  const byProviderModel: Record<
    string,
    { kind: string; provider: string; model: string; totalNano: string; calls: number }
  > = {};
  for (const s of spans) {
    if (AGGREGATORS.has(s.kind)) continue;
    if (!s.costNano) continue;
    const provider = attrString(s.attributes, 'provider');
    const model = attrString(s.attributes, 'model');
    if (!provider || !model) continue;
    const key = `${s.kind}:${provider}:${model}`;
    const acc = byProviderModel[key] ?? {
      kind: s.kind,
      provider,
      model,
      totalNano: '0',
      calls: 0,
    };
    acc.totalNano = sumNano([acc.totalNano, s.costNano]);
    acc.calls += 1;
    byProviderModel[key] = acc;
  }

  // Por turn.
  const turns = (d.turns ?? []).map((t) => {
    const turnSpan = spans.find(
      (s) => s.kind === 'turn' && attrString(s.attributes, 'turn_id') === t.id,
    );
    return {
      turnIndex: t.turnIndex,
      turnId: t.id,
      totalNano: turnSpan?.costNano ?? null,
      totalUsd: turnSpan?.costNano ? nanoToUsd(turnSpan.costNano) : null,
      llmCostNano: attrString(turnSpan?.attributes ?? {}, 'llm_cost_nano'),
      sttCostNano: attrString(turnSpan?.attributes ?? {}, 'stt_cost_nano'),
      ttsCostNano: attrString(turnSpan?.attributes ?? {}, 'tts_cost_nano'),
      pronunciationCostNano: attrString(turnSpan?.attributes ?? {}, 'pronunciation_cost_nano'),
    };
  });

  // Tokens totales.
  const tokenTotals = (d.llmCalls ?? []).reduce(
    (acc, lc) => ({
      input: acc.input + lc.inputTokens,
      output: acc.output + lc.outputTokens,
      cacheRead: acc.cacheRead + lc.cacheReadTokens,
      cacheWrite: acc.cacheWrite + lc.cacheCreateTokens,
    }),
    { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
  );

  return {
    session: { id: d.session?.id, userEmail: d.session?.userEmail, moduleId: d.session?.moduleId },
    totals: {
      nano: totalNano,
      usd: nanoToUsd(totalNano),
      tokens: tokenTotals,
    },
    byKind,
    byProviderModel: Object.values(byProviderModel),
    byTurn: turns,
  };
}

function toTiming(d: SessionDump): unknown {
  const spans = d.spans ?? [];
  const sessionSpan = spans.find((s) => s.kind === 'session');

  const turns = (d.turns ?? []).map((t) => {
    const turnSpan = spans.find(
      (s) => s.kind === 'turn' && attrString(s.attributes, 'turn_id') === t.id,
    );
    const llmSpan = turnSpan
      ? spans.find((s) => s.parentId === turnSpan.id && s.kind === 'llm')
      : null;
    const sttSpan = turnSpan
      ? spans.find((s) => s.parentId === turnSpan.id && s.kind === 'stt')
      : null;
    const ttsSpan = turnSpan
      ? spans.find((s) => s.parentId === turnSpan.id && s.kind === 'tts')
      : null;
    const playback = turnSpan
      ? spans.find((s) => s.parentId === turnSpan.id && s.kind === 'composite')
      : null;
    return {
      turnIndex: t.turnIndex,
      turnDurationMs: t.durationMs,
      timeToFirstAudioMs: t.timeToFirstAudioMs,
      stt: sttSpan
        ? { durationMs: sttSpan.durationMs, textLength: attrNumber(sttSpan.attributes, 'text_length') }
        : null,
      llm: llmSpan
        ? {
            durationMs: llmSpan.durationMs,
            ttftMs: attrNumber(llmSpan.attributes, 'ttft_ms'),
            outputTokens: attrNumber(llmSpan.attributes, 'output_tokens'),
          }
        : null,
      tts: ttsSpan
        ? {
            durationMs: ttsSpan.durationMs,
            characters: attrNumber(ttsSpan.attributes, 'characters'),
          }
        : null,
      botPlaybackMs: playback?.durationMs ?? null,
      interrupted: t.interrupted,
    };
  });

  return {
    session: {
      startedAt: d.session?.startedAt,
      endedAt: d.session?.endedAt,
      totalDurationMs: sessionSpan?.durationMs ?? null,
      endedReason: d.session?.endedReason,
    },
    turnCount: turns.length,
    turns,
  };
}

function toErrors(d: SessionDump): unknown {
  const spans = d.spans ?? [];
  const errorSpans = spans.filter((s) => s.isError);
  if (errorSpans.length === 0) {
    return {
      session: { id: d.session?.id, userEmail: d.session?.userEmail },
      errorCount: 0,
      note: 'No errors in this session.',
    };
  }
  const spanById = new Map(spans.map((s) => [s.id, s]));
  const items = errorSpans.map((s) => {
    const parent = s.parentId ? spanById.get(s.parentId) ?? null : null;
    const turnAncestor = (() => {
      let cur = parent;
      while (cur) {
        if (cur.kind === 'turn') return cur;
        cur = cur.parentId ? spanById.get(cur.parentId) ?? null : null;
      }
      return null;
    })();
    return {
      span: {
        id: s.id,
        kind: s.kind,
        name: s.name,
        errorKind: s.errorKind,
        attributes: s.attributes,
        startedAt: s.startedAt,
        durationMs: s.durationMs,
      },
      parent: parent
        ? { id: parent.id, kind: parent.kind, name: parent.name }
        : null,
      turnContext: turnAncestor
        ? {
            id: turnAncestor.id,
            turnIndex: attrNumber(turnAncestor.attributes, 'turn_index'),
            turnId: attrString(turnAncestor.attributes, 'turn_id'),
          }
        : null,
    };
  });
  return {
    session: { id: d.session?.id, userEmail: d.session?.userEmail, moduleId: d.session?.moduleId },
    errorCount: items.length,
    items,
  };
}

function toAudio(d: SessionDump): unknown {
  const audio = d.audio ?? [];
  const turns = d.turns ?? [];
  const expected = turns.length * 2;
  return {
    session: { id: d.session?.id, userEmail: d.session?.userEmail, moduleId: d.session?.moduleId },
    summary: {
      turns: turns.length,
      expectedFiles: expected,
      presentFiles: audio.length,
      missingFiles: Math.max(0, expected - audio.length),
      totalBytes: audio.reduce((acc, a) => acc + a.bytes, 0),
    },
    files: audio,
  };
}

function toClaudeBundle(d: SessionDump): unknown {
  // Map turnId → corrections del tool, para anexar correcciones al turn
  // correspondiente en `conversation`. La búsqueda no asume orden: el
  // promptLogs y el turns DB pueden venir desalineados si hubo retries.
  const correctionsByTurnId = new Map<
    string,
    NonNullable<NonNullable<SessionDump['promptLogs']>[number]['toolCalls']>
  >();
  for (const pl of d.promptLogs ?? []) {
    if (pl.turnId && pl.toolCalls) correctionsByTurnId.set(pl.turnId, pl.toolCalls);
  }
  const turns = (d.turns ?? []).map((t) => {
    const tc = correctionsByTurnId.get(t.id);
    return {
      turnIndex: t.turnIndex,
      userText: t.userText,
      assistantText: t.assistantText,
      durationMs: t.durationMs,
      interrupted: t.interrupted,
      ...(tc?.corrections && tc.corrections.length > 0
        ? { corrections: tc.corrections }
        : {}),
      ...(tc?.pronunciation_corrections && tc.pronunciation_corrections.length > 0
        ? { pronunciationCorrections: tc.pronunciation_corrections }
        : {}),
    };
  });
  const errorSpans = (d.spans ?? []).filter((s) => s.isError);
  const sessionSpan = (d.spans ?? []).find((s) => s.kind === 'session');
  const persona = attrString(sessionSpan?.attributes ?? {}, 'persona_slug');
  const voiceId = attrString(sessionSpan?.attributes ?? {}, 'voice_id');
  const promptSummaries = (d.promptLogs ?? []).map((pl) => ({
    turnId: pl.turnId,
    systemPrompt: pl.systemPrompt,
    ...(pl.dynamicSystem ? { dynamicSystem: pl.dynamicSystem } : {}),
    messages: pl.messages,
    responseText: pl.responseText,
    ...(pl.toolCalls ? { toolCalls: pl.toolCalls } : {}),
  }));

  // Fase 28 — Llamadas LLM AUXILIARES por turno (translator + futuras).
  // El bundle de `promptingByTurn` solo trae el LLM principal del turno
  // (via prompt_logs). Las llamadas auxiliares viven sólo en `spans` con
  // `kind='llm'` y `name='anthropic.translate'` (o similar futuro). Acá
  // las cruzamos contra los turn spans para anclarlas por turno.
  const AUXILIARY_LLM_SPAN_NAMES = new Set(['anthropic.translate']);
  const turnSpanIdToTurnId = new Map<string, string>();
  for (const s of d.spans ?? []) {
    if (s.kind !== 'turn') continue;
    const turnId = attrString(s.attributes, 'turn_id');
    if (turnId) turnSpanIdToTurnId.set(s.id, turnId);
  }
  const turnIdToTurnIndex = new Map<string, number>();
  for (const t of d.turns ?? []) turnIdToTurnIndex.set(t.id, t.turnIndex);

  const auxiliaryLlmCallsByTurn: Array<{
    turnIndex: number | null;
    name: string;
    durationMs: number;
    costUsd: number;
    inputTokens: number | null;
    outputTokens: number | null;
    targetLang?: string;
  }> = [];
  for (const s of d.spans ?? []) {
    if (s.kind !== 'llm' || !AUXILIARY_LLM_SPAN_NAMES.has(s.name)) continue;
    const turnId = s.parentId ? turnSpanIdToTurnId.get(s.parentId) ?? null : null;
    const turnIndex = turnId ? turnIdToTurnIndex.get(turnId) ?? null : null;
    const targetLang = attrString(s.attributes, 'target_lang');
    auxiliaryLlmCallsByTurn.push({
      turnIndex,
      name: s.name,
      durationMs: s.durationMs,
      costUsd: nanoToUsd(s.costNano),
      inputTokens: attrNumber(s.attributes, 'input_tokens'),
      outputTokens: attrNumber(s.attributes, 'output_tokens'),
      ...(targetLang ? { targetLang } : {}),
    });
  }

  return {
    purpose: 'Bundle para análisis con IA. Incluye conversación + prompts + responses + errores. Sin telemetría cruda.',
    session: {
      id: d.session?.id,
      moduleId: d.session?.moduleId,
      startedAt: d.session?.startedAt,
      endedAt: d.session?.endedAt,
      endedReason: d.session?.endedReason,
      persona,
      voiceId,
    },
    conversation: turns,
    promptingByTurn: promptSummaries,
    ...(auxiliaryLlmCallsByTurn.length > 0
      ? { auxiliaryLlmCallsByTurn }
      : {}),
    errors:
      errorSpans.length === 0
        ? null
        : errorSpans.map((s) => ({
            kind: s.kind,
            name: s.name,
            errorKind: s.errorKind,
            attributes: s.attributes,
          })),
  };
}

function toMarkdown(d: SessionDump): string {
  const lines: string[] = [];
  const s = d.session;
  lines.push(`# Sesión ${s?.id ?? '?'}`);
  if (s?.userEmail) lines.push(`- **User**: ${s.userEmail}`);
  if (s?.moduleId) lines.push(`- **Module**: ${s.moduleId}`);
  if (s?.startedAt) lines.push(`- **Started**: ${s.startedAt}`);
  if (s?.endedAt) lines.push(`- **Ended**: ${s.endedAt}`);
  if (s?.endedReason) lines.push(`- **Reason**: ${s.endedReason}`);
  lines.push('');
  lines.push('## Conversación');
  lines.push('');
  for (const t of d.turns ?? []) {
    lines.push(`### Turn ${t.turnIndex}`);
    if (t.userText) {
      lines.push('');
      lines.push(`**User:** ${t.userText}`);
    }
    if (t.assistantText) {
      lines.push('');
      lines.push(`**Assistant:** ${t.assistantText}`);
    }
    lines.push('');
  }
  return lines.join('\n');
}

export const COPY_VIEW_OPTIONS: Array<{
  view: CopyView;
  label: string;
  description: string;
  /** Tipo de payload — afecta el mime type al copiar. */
  format: 'json' | 'text';
}> = [
  {
    view: 'full',
    label: 'Todo (full dump)',
    description: 'JSON completo con sesión, turns, llm_calls, spans, audio y prompt_logs.',
    format: 'json',
  },
  {
    view: 'transcript',
    label: 'Conversación (transcript)',
    description: 'Solo turns con texto del user y del assistant + timing.',
    format: 'json',
  },
  {
    view: 'prompts',
    label: 'Prompts & responses',
    description: 'System prompt + messages + response_text por turno.',
    format: 'json',
  },
  {
    view: 'costs',
    label: 'Costos',
    description: 'Total, por kind, por provider+model, por turn, totales de tokens.',
    format: 'json',
  },
  {
    view: 'timing',
    label: 'Tiempos / Performance',
    description: 'Latencias por etapa (STT, LLM, TTS), TTFT, duración del turn.',
    format: 'json',
  },
  {
    view: 'errors',
    label: 'Errores',
    description: 'Spans con is_error=true + parent + contexto del turn.',
    format: 'json',
  },
  {
    view: 'audio',
    label: 'Audio / Replay manifest',
    description: 'Archivos persistidos por turno (kind, sample rate, duración, bytes).',
    format: 'json',
  },
  {
    view: 'claude',
    label: 'Para Claude (debugging bundle)',
    description: 'Conversación + prompts + errores + persona/módulo. Sin telemetría cruda.',
    format: 'json',
  },
  {
    view: 'markdown',
    label: 'Markdown (Slack-ready)',
    description: 'Conversación renderizada como texto plano markdown.',
    format: 'text',
  },
];
